<template>
  <section v-if="records.length" class="attendance-section">
    <h2>Chick-fil-A Friday Attendance</h2>
    <p class="methodology">
      Attendance is reconstructed from Chick-fil-A expense dates and qualifying work
      activity. Historical totals are best estimates and may be updated as additional
      records become available. Crew members must have at least 10 eligible Fridays
      before joining the leaderboard.
    </p>

    <div class="attendance-table" role="table" aria-label="Chick-fil-A Friday attendance">
      <div class="attendance-header" role="row">
        <span role="columnheader">Crew Member</span>
        <span role="columnheader">Eligible Fridays</span>
        <span role="columnheader">Fridays Attended</span>
        <span role="columnheader">Attendance</span>
      </div>
      <article
        v-for="record in records"
        :key="record.employeeSlug"
        class="attendance-row"
        role="row"
      >
        <div role="cell" class="person">
          <v-img
            v-if="record.headshot"
            :src="record.headshot | mediaSrc"
            :alt="record.employeeName"
            height="42"
            width="42"
            class="avatar"
          />
          <div class="person-copy">
            <router-link
              v-if="record.passportSlug"
              class="passport-link"
              :to="{ name: 'passport', params: { employeeSlug: record.passportSlug } }"
              :aria-label="`View ${record.employeeName}'s Reckon Passport`"
            >
              <strong>{{ record.employeeName }}</strong>
            </router-link>
            <strong v-else>{{ record.employeeName }}</strong>
            <small>{{ record.rankingLabel }}</small>
          </div>
        </div>
        <span role="cell" class="eligible-value">{{ record.eligibleEvents }}</span>
        <span role="cell" class="attended-value">{{ record.attendedEvents }}</span>
        <div role="cell" class="attendance-value">
          <strong>{{ record.attendancePercentage }}%</strong>
          <div
            role="progressbar"
            :aria-label="`${record.employeeName} Chick-fil-A Friday attendance`"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="record.attendancePercentage"
          >
            <i :style="{ width: `${record.attendancePercentage}%` }" />
          </div>
        </div>
        <p v-if="showMattMessage(record)" class="matt-message">
          Still waiting on Matt to make his first appearance. Even free chicken isn't
          enough to lure him out of his WFH situation.
        </p>
      </article>
    </div>
  </section>
</template>

<script>
import attendanceUtilities from '@/lib/traditions/attendance.cjs';

export default {
  name: 'AttendanceBoard',
  props: {
    records: { type: Array, default: () => [] },
  },
  methods: {
    showMattMessage(record) {
      return attendanceUtilities.shouldShowMattFirstAppearance(record);
    },
  },
};
</script>

<style scoped>
.attendance-section { border-top: 1px solid #292929; margin-top: 64px; padding-top: 54px; }
h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: 2.4rem; margin: 0 0 12px; }
.methodology { color: #929292; font-size: .84rem; line-height: 1.55; max-width: 850px; }
.attendance-table { border: 1px solid #303030; margin-top: 26px; }
.attendance-header, .attendance-row { align-items: center; display: grid; gap: 16px; grid-template-columns: 1.5fr repeat(3, .7fr); padding: 16px 20px; }
.attendance-header { background: #1b1b1b; color: #aaa; font-size: .7rem; font-weight: 900; text-transform: uppercase; }
.attendance-row { background: #111; color: #ddd; }
.attendance-row + .attendance-row { border-top: 1px solid #292929; }
.person { align-items: center; display: flex; gap: 12px; }
.person strong { color: #fff; display: block; }
.person small { color: #f0b13c; display: block; font-size: .7rem; }
.passport-link { display: block; text-decoration: none; width: fit-content; }
.passport-link:hover strong, .passport-link:focus-visible strong { color: #e23232; }
.passport-link:focus-visible { outline: 2px solid #e23232; outline-offset: 4px; }
.avatar { border-radius: 50%; flex: 0 0 auto; }
.attendance-value > strong { color: #fff; }
.attendance-value [role="progressbar"] { background: #303030; border-radius: 999px; height: 7px; margin-top: 7px; overflow: hidden; }
.attendance-value i { background: #e23232; display: block; height: 100%; }
.matt-message { color: #aaa; font-size: .78rem; font-style: italic; grid-column: 1 / -1; margin: 0; padding-left: 54px; }
@media (max-width: 700px) {
  .attendance-header { display: none; }
  .attendance-row { align-items: start; border-radius: 8px; grid-template-columns: 1fr 1fr; margin: 12px; padding: 18px; }
  .person { grid-column: 1 / -1; }
  .eligible-value::before, .attended-value::before { color: #777; display: block; font-size: .7rem; text-transform: uppercase; }
  .eligible-value::before { content: "Eligible"; }
  .attended-value::before { content: "Attended"; }
  .attendance-value { grid-column: 1 / -1; }
  .matt-message { padding-left: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .attendance-value i { transition: none; }
}
</style>
